// 3rd party libraries
import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { sum } from 'lodash';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useTranslation } from 'react-i18next';

// Components
import { CartListComponent, Loader, Typography } from 'components';

// Helpers
import { currency } from 'common/helpers/v1';

// Types
import { Product } from 'types/parts';
import { CartListType, CartPurchasable } from 'types/parts/cart';
import { MaintenanceEventTableRow, PMPurchasable } from 'types/maintenance';

// Api
import { useGetProductsQuery } from 'api';

// Analytics
import { IcoCart } from 'icons/indexed-icons/IcoCart';

import { ToastMsg } from 'common/components/Toast';

interface MaintenanceEventPartsDetailProps {
  maintenanceEvent?: MaintenanceEventTableRow;
  cartViewType: string;
  onCartViewType: (cartViewType: CartListType.VIEW_TYPE_LESS | CartListType.VIEW_TYPE_MORE) => void;
  onSubmitClick: () => void;
  cartList: CartPurchasable[];
  updateCartList: (data: CartPurchasable[]) => void;
}

const PMKitContainer = styled.div`
  padding: 1rem 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-y: auto;
`;
const Footer = styled.div`
  display: flex;
  justify-content: end;
`;

const TotalContainer = styled.div`
  p {
    margin: 0;
  }
  height: 2.5rem;
`;

const MaintenanceEventPartsDetail = ({
  maintenanceEvent,
  onCartViewType,
  cartList,
  updateCartList
}: MaintenanceEventPartsDetailProps): ReactElement => {
  const { t } = useTranslation(['fpns']);
  const {
    data: products,
    error: partError,
    isFetching: productsAreLoading
  } = useGetProductsQuery(
    maintenanceEvent?.purchasables && maintenanceEvent?.purchasables.length > 0
      ? {
          productIds: maintenanceEvent?.purchasables.map(
            (purchasable) => purchasable.purchasableId
          ),
          includeAssets: false,
          machineUuid: maintenanceEvent.machineId
        }
      : skipToken
  );
  if (partError) {
    ToastMsg({
      heading: 'Warning',
      type: 'warning',
      message: t('parts_cannot_be_retrieved'),
      toastId: 'parts-invalid'
    });
  }
  useEffect(() => {
    if (
      !productsAreLoading &&
      products !== undefined &&
      maintenanceEvent !== undefined &&
      maintenanceEvent.purchasables !== undefined
    ) {
      updateCartList(
        getCartPurchasableItems(
          products.items,
          maintenanceEvent.purchasables,
          maintenanceEvent.machineId || 'no-machine-id'
        )
      );
    }
  }, [products, productsAreLoading]);

  const selectedItems = cartList.filter((cartItem) => cartItem.selected);
  const pmPartsTotalPrice = cartList
    ? sum(
        selectedItems.map(
          (p) => ((p.item?.custPrice ? p.item.custPrice : p.item.price) || 0) * p.quantity
        )
      )
    : 0;
  const cartChangeHandler = (cartList: CartPurchasable[]) => {
    updateCartList(cartList);
  };

  return (
    <PMKitContainer>
      <Typography as="h3" size="1rem" weight="semi-bold">
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <IcoCart width="24" />
          {t('order_pm_kit')}
        </div>
      </Typography>
      <Typography size="0.8125rem">{maintenanceEvent?.description}</Typography>
      {productsAreLoading ? (
        <Loader />
      ) : (
        cartList &&
        cartList.length > 0 && (
          <CartListComponent
            pmParts={cartList}
            viewType={CartListType.VIEW_TYPE_LESS}
            selectionType={CartListType.SELECTION_REMOVE}
            onCartViewType={onCartViewType}
            onQtyChange={cartChangeHandler}
            onSelectChange={cartChangeHandler}
          />
        )
      )}
      {!productsAreLoading && (
        <Footer>
          <TotalContainer>
            <Typography size="0.8125rem">{t('total', { ns: 'common' })}</Typography>
            <Typography size="1.125rem">
              {String(
                currency(
                  pmPartsTotalPrice,
                  cartList && cartList.length > 0 ? cartList[0].item.priceUnit : undefined
                )
              )}
            </Typography>
          </TotalContainer>
        </Footer>
      )}
    </PMKitContainer>
  );
};

const getCartPurchasableItems = (
  products: Product[],
  purchasables: PMPurchasable[],
  machineId: string
): CartPurchasable[] => {
  const retList: CartPurchasable[] = [];
  for (const purchasable of purchasables) {
    const product = products.find((p) => purchasable.purchasableId == p.id);
    if (product !== undefined) {
      retList.push({
        item: product,
        quantity: purchasable.quantity,
        verified: false,
        selected: true,
        verificationComments: [],
        groupId: machineId
      });
    } else {
      console.warn(
        `Maintenance Event ${purchasable.maintenanceEventId} referenced purchasable ${purchasable.purchasableId} but no product information could be found!`
      );
    }
  }
  return retList;
};

export default MaintenanceEventPartsDetail;
